import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useScrollEdges } from '../../scroll/scroll-affordance';
import { useLocation, Navigate } from 'react-router-dom';
import {
  ContentWrapper,
  LayoutContainer,
  NavbarAndContentContainer,
  ContentAndButtonsContainer,
} from '../../layout/layout-container.styled';
import {
  DashboardMainMenu,
  dashboardSectionForPath,
  dashboardTitleForPath,
} from './dashboard-shared-components/dashboard-main-menu';
import { ContentTransition } from '../../route-transition/route-transition';
import { RedirectWithReason } from '../../auth/require-role';
import { DashboardHeading } from './dashboard-shared-components/dashboard-heading';
import { DashboardContentContainer } from './dashboard.styled';
import { DashboardAddNewBuilding } from './dashboard-buildings-components/dashboard-add-new-building';
import { DashboardBuildings } from './dashboard-buildings-components/dashboard-buildings';
import { DashboardUsers } from './dashboard-users-components/dashboard-users';
import {
  User,
  UserWithBuildingsObjects,
} from '../../../interfaces/user-interfaces';
import { DashboardAddNewUser } from './dashboard-users-components/dashboard-add-new-user';
import { DashboardEditUserById } from './dashboard-users-components/dashboard-edit-user-by-id';
import { DashboardTenants } from './dashboard-tenants-components/dashboard-tenants';
import { DashboardAddNewTenant } from './dashboard-tenants-components/dashboard-add-new-tenant';
import { DashboardBilling } from './dashboard-billing-components/dashboard-billing';
import { useAuth } from '../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../auth/permissions';
import { DashboardPortfolios } from './dashboard-portfolios-components/dashboard-portfolios';
import { DashboardUnits } from './dashboard-units-components/dashboard-units';
import { DashboardUnitDetail } from './dashboard-units-components/dashboard-unit-detail';
import { DashboardBuildingRoster } from './dashboard-units-components/dashboard-building-roster';
import { DashboardScopes } from './dashboard-scopes-components';
import { DashboardShifts } from './dashboard-shifts-components/dashboard-shifts';
import { DashboardScreen } from './dashboard-screen/dashboard-screen';
import { DashboardPasses } from './dashboard-passes-components/dashboard-passes';
import { DashboardStays } from './dashboard-stays-components/dashboard-stays';
import { SyncReviewPage } from '../stays/sync/sync-review-page';
import { SetupWizard } from '../stays/setup/setup-wizard';
import { MessageLibraryPage } from '../stays/messages/message-library-page';
import { PropertiesPage } from '../stays/properties/properties-page';
import { StaysBillingPage } from '../stays/billing/stays-billing-page';
import { YourDataPage } from '../stays/your-data/your-data-page';
import { TeamPage } from '../stays/team/team-page';
import { GuestCardsPage } from '../stays/team/guest-cards-page';
import {
  STAYS_LISTING_PATH,
  StaysListingShell,
  sectionOf,
} from '../stays/listing/stays-listing-shell';
import { NotFound } from '../not-found/not-found';
import {
  DESK_NOT_IN_PLAN_REASON,
  deskInPlan,
  isDeskDashboardHome,
  isDeskDashboardPath,
  isBuildingsDashboardPath,
  STAYS_HOME,
  STAYS_PROPERTIES,
  staysOnlyAccount,
} from '../../../utils/products';

// The whole dashboard mounts on the single '/dashboard/*' route, so a unit id in
// the path is part of the splat rather than a route param — useParams() inside
// DashboardUnitDetail would come back empty. Read it off the pathname here and
// hand it down explicitly.
const UNIT_DETAIL_PATH = /^\/dashboard\/units\/(\d+)\/?$/;
// Lobbero Stays (plan S1; plan S4's listing shell): a listing page and its
// sections — /dashboard/stays/<uuid>[/overview|/stays[/<uuid>]|/calendar|/new].
// STAYS_LISTING_PATH lives with the shell (stays-listing-routes.ts). A uuid
// only — any other tail falls to the switch's default and is a 404. Later
// plans' organisation-wide section pages use non-uuid slugs
// (/dashboard/stays/<slug>) and their own `case`, so this never claims them
// (stays-section.tsx, STAYS_SECTIONS).
// Independent Stays plan ISR: the setup wizard, /dashboard/stays/setup[/<listing uuid>].
const STAYS_SETUP_PATH = /^\/dashboard\/stays\/setup(?:\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}))?\/?$/i;

/* The layer ContentTransition puts around the switch's output. A flex column
   so a screen that asks for `flex: 1` fills the pane; the scroll container is
   still DashboardContentContainer OUTSIDE this layer, so the scrollTop reset
   below keeps working on the same element it always did. */
const CONTENT_LAYER_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
};

export const Dashboard = () => {
  const { permissions, user } = useAuth();
  // Independent Stays IS1: a Stays-only organisation has no Desk sections.
  const hasDesk = deskInPlan(user);
  const canCreateBuilding = can(permissions, PERMISSIONS.BUILDING_CREATE);
  const canCreateUser = can(permissions, PERMISSIONS.USER_CREATE);
  const canCreateTenant = can(permissions, PERMISSIONS.TENANT_CREATE);
  const canReadPortfolio = can(permissions, PERMISSIONS.PORTFOLIO_READ);
  const canReadUnit = can(permissions, PERMISSIONS.UNIT_READ);
  const canReadOccupancy = can(permissions, PERMISSIONS.OCCUPANCY_READ);
  const canReadScope = can(permissions, PERMISSIONS.SCOPE_READ);
  const canReadBilling = can(permissions, PERMISSIONS.BILLING_READ);
  const canManageBilling = can(permissions, PERMISSIONS.BILLING_MANAGE);
  const canReadUsers = can(permissions, PERMISSIONS.USER_READ);
  // The Shifts tab mirrors its own backend gate exactly (GET /shifts/history and
  // GET /shifts/:id/detail are `user:read`), rather than picking a role list that
  // happens to line up today. A concierge holds shift:clock and must still not
  // reach a screen that reports on a colleague's attendance.
  const canReadShifts = can(permissions, PERMISSIONS.USER_READ);
  // The Analytics Hub mirrors its own backend gate exactly: all four
  // /api/analytics/* routes are `requirePermission(analytics:read)`, so the
  // screen is gated on the capability rather than on a role list that happens to
  // line up today. Without it the tile is hidden AND the route bounces, so there
  // is no path to a screen whose every request 403s.
  const canReadAnalytics = can(permissions, PERMISSIONS.ANALYTICS_READ);
  // The visitor-pass register (Plan 3 RP-3.7) mirrors GET /buildings/:b/passes.
  const canReadPasses = can(permissions, PERMISSIONS.VISITORPASS_READ);
  // Lobbero Stays (plan S1). The permission gates the route; whether the
  // ORGANISATION has Stays is the screen's own first question (it renders
  // "not switched on" rather than redirecting, so a typed address is a page).
  const canReadStays = can(permissions, PERMISSIONS.STAYS_READ);
  const canManageStays = can(permissions, PERMISSIONS.STAYS_MANAGE);
  // Independent Stays IS5: Properties is stays:promote (never a co-host's); a
  // co-host has their Guest cards.
  const canPromoteStays = can(permissions, PERMISSIONS.STAYS_PROMOTE);
  const canReadGuestCards = can(permissions, PERMISSIONS.STAYS_GUEST_CARDS);
  // WP-26. A counter, not a boolean: the add-user and add-resident forms are
  // MODALS OVER THEIR LISTS, rendered from an unkeyed fragment here. React
  // reconciles that fragment's children against the parent's list, so on the way
  // back to the bare list route the list component matches at index 0 by type
  // and its fiber is REUSED — it never unmounts, so useResource's mount effect
  // never fires again and the person who just created a user is returned to a
  // list without them. Bumping a number the list watches is the explicit
  // refresh; the alternative, keying the content slot, would throw away scroll
  // position and remount every screen on every navigation.
  //
  // Passed on the BARE routes too, not only the modal ones. The prop must not
  // fall back to its default on the render that follows the save, or the list
  // would refetch because the number went 1 -> 0, which is the right outcome for
  // the wrong reason and fetches twice.
  const [usersRefreshToken, setUsersRefreshToken] = useState(0);
  const [tenantsRefreshToken, setTenantsRefreshToken] = useState(0);

  const [editableUserData, setEditableUserData] = useState<
    UserWithBuildingsObjects | User | undefined
  >();

  const location = useLocation();

  const updateEditableUserData = useCallback(
    (userToBeUpdated: UserWithBuildingsObjects | User) => {
      setEditableUserData(userToBeUpdated);
    },
    []
  );

  const renderDashboardContentAccordingUrl = (currentUrl: string) => {
    // A STAYS-ONLY ORGANISATION (Independent Stays IS1) has no Desk home and no
    // Desk sections. The bare /dashboard (the logo, every 'here is your
    // dashboard' redirect below) goes to Stays without a word; a typed Desk
    // section says why. The API refuses them with DESK_NOT_IN_PLAN regardless.
    if (!hasDesk && isDeskDashboardPath(currentUrl)) {
      return isDeskDashboardHome(currentUrl) ? (
        <Navigate to={STAYS_HOME} replace />
      ) : (
        <RedirectWithReason to={STAYS_HOME} reason={DESK_NOT_IN_PLAN_REASON} />
      );
    }

    // Buildings and Units are the same rows as Properties in Stays: without
    // Desk a typed address opens Properties, without a word (no building word
    // reaches a Stays-only screen).
    if (!hasDesk && isBuildingsDashboardPath(currentUrl)) {
      return <Navigate to={STAYS_PROPERTIES} replace />;
    }

    // Handled ahead of the switch because the unit id makes the path variable —
    // there is no fixed string to `case` on.
    const unitDetail = UNIT_DETAIL_PATH.exec(currentUrl);

    if (unitDetail) {
      if (canReadUnit) {
        return <DashboardUnitDetail unitId={Number(unitDetail[1])} />;
      }

      return (
        <RedirectWithReason
          to="/dashboard"
          reason="That unit is not available for your role. Here is your dashboard."
        />
      );
    }

    // Same splat reason as the unit page: the listing id, its section and a
    // reservation id are all part of the '/dashboard/*' tail, so useParams()
    // inside a section would come back empty. Parsed here and handed down.
    // Independent Stays plan ISR: the listing setup wizard,
    // /dashboard/stays/setup[/<listing uuid>]?step=<key> (stays:promote inside).
    const staysSetup = STAYS_SETUP_PATH.exec(currentUrl);

    if (staysSetup) {
      if (canReadStays) {
        return <SetupWizard key={staysSetup[1] || 'new'} listingId={staysSetup[1] ? staysSetup[1].toLowerCase() : null} />;
      }

      return (
        <RedirectWithReason
          to="/dashboard"
          reason="Stays is not available for your role. Here is your dashboard."
        />
      );
    }

    const staysListing = STAYS_LISTING_PATH.exec(currentUrl);

    if (staysListing) {
      if (canReadStays) {
        return (
          <StaysListingShell
            listingId={staysListing[1].toLowerCase()}
            section={sectionOf(staysListing[2] || 'overview')}
            reservationId={staysListing[3] ? staysListing[3].toLowerCase() : null}
          />
        );
      }

      return (
        <RedirectWithReason
          to="/dashboard"
          reason="Stays is not available for your role. Here is your dashboard."
        />
      );
    }

    switch (currentUrl) {
      // Editing a building is a MODAL over this list, addressed by
      // '?edit=<id>'. A query param rather than a path segment because the
      // switch below keys on the pathname alone, and because the scroll reset
      // at the bottom of this file fires on every pathname change — a path
      // segment would jump the list to the top behind the modal.
      case '/dashboard/buildings':
        return <DashboardBuildings />;
      case '/dashboard/buildings/':
        return <DashboardBuildings />;
      case '/dashboard/users':
      case '/dashboard/users/':
        // Hiding the link is not a guard — same pairing as Billing above.
        if (!canReadUsers) {
          return (
            <RedirectWithReason
              to="/dashboard"
              reason="People and access are not available for your role. Here is your dashboard."
            />
          );
        }

        return (
          <DashboardUsers
            updateEditableUserData={updateEditableUserData}
            refreshToken={usersRefreshToken}
          />
        );

      /* ── THE DASHBOARD, AND THE FOUR ROUTES IT REPLACES ──
         /dashboard/home, /dashboard/analytics, /dashboard/reports and
         /dashboard/view-edit-export are one screen now. The old paths RENDER it
         rather than redirecting to it, and that is a deliberate choice between
         two options that look equivalent and are not:

           · A `<Navigate>` would rewrite the address bar, so every bookmark,
             every link in an email, and every browser-history entry anybody has
             would silently become a different URL — and the back button would
             land on the redirect and bounce forward again.
           · Rendering keeps the address the reader typed. The link they saved
             still works, still says what they saved, and the filter query
             string on it is still read, because the Dashboard's filter IS
             the query string.

         The one exception is /dashboard/analytics: it stays gated on
         `analytics:read` exactly as it was, so a caller who could not open it
         yesterday cannot open it today by a route that used to 403. The screen
         itself hides the Trends tab and fires no analytics request for such a
         caller regardless — this is the belt to that pair of braces. */
      case '/dashboard/reports':
      case '/dashboard/reports/':
      case '/dashboard/view-edit-export':
      case '/dashboard/view-edit-export/':
      case '/dashboard/home':
      case '/dashboard/home/':
        return <DashboardScreen />;

      case '/dashboard/analytics':
      case '/dashboard/analytics/':
        if (canReadAnalytics) {
          return <DashboardScreen />;
        }

        return (
          <RedirectWithReason
            to="/dashboard"
            reason="Analytics is not available for your role. Here is your dashboard."
          />
        );

      case '/dashboard/buildings/add-new-property':
      case '/dashboard/buildings/add-new-property/':
        if (canCreateBuilding) {
          return <DashboardAddNewBuilding />;
        }

        return <Navigate to="/dashboard/buildings" replace />;
      // Adding a user is a MODAL OVER THE LIST, and the list is rendered here
      // rather than left out: a form in a layer with nothing behind it is a
      // page wearing a scrim. It keeps its own path (not the buildings screen's
      // '?edit=' query param) because this list has no scroll position worth
      // preserving — it is reached from a button at its top.
      case '/dashboard/users/add-new-user':
      case '/dashboard/users/add-new-user/':
        if (canCreateUser) {
          return (
            <>
              <DashboardUsers
                updateEditableUserData={updateEditableUserData}
                refreshToken={usersRefreshToken}
              />
              <DashboardAddNewUser
                onSaved={() => setUsersRefreshToken(token => token + 1)}
              />
            </>
          );
        }

        return <Navigate to="/dashboard/users" replace />;
      // The old edit-property PAGE. It only ever worked when the list had just
      // handed the building down through React state, so every deep link and
      // every refresh already bounced straight back here — the redirect makes
      // that explicit instead of routing through a form that empties itself.
      // Bookmarks and the browser's history survive; they land on the list.
      case '/dashboard/buildings/edit-property':
      case '/dashboard/buildings/edit-property/':
        return <Navigate to="/dashboard/buildings" replace />;

      case '/dashboard/users/edit-user':
        return <DashboardEditUserById editableUserData={editableUserData} />;
      case '/dashboard/users/edit-user/':
        return <DashboardEditUserById editableUserData={editableUserData} />;
      /* ── RESIDENTS (WP-31 / MR-18) ──
         The screen has been called Residents since the label was changed
         (dashboard-constants.tsx:22) and the address still said /tenants, so the
         one URL a reader would guess — and the one the product's own copy
         teaches them — fell through the switch below to the Dashboard home. That
         is the address bar disagreeing with the page, and there was no way to
         find out which of the two was wrong.

         /dashboard/residents is now the canonical address. The old one REDIRECTS
         rather than renders, which is the opposite of the choice made for
         /dashboard/analytics a few cases above, and deliberately: those four
         paths are still a correct name for what they show, so keeping the
         reader's URL costs nothing. `tenants` is not a name this product uses
         any more, so the address bar should stop saying it — and every saved
         link still arrives at the right screen. */
      case '/dashboard/residents':
      case '/dashboard/residents/':
        return <DashboardTenants refreshToken={tenantsRefreshToken} />;
      // A modal over the list, for the same reason as add-new-user above.
      case '/dashboard/residents/add-new-resident':
      case '/dashboard/residents/add-new-resident/':
        if (canCreateTenant) {
          return (
            <>
              <DashboardTenants refreshToken={tenantsRefreshToken} />
              <DashboardAddNewTenant
                onSaved={() => setTenantsRefreshToken(token => token + 1)}
              />
            </>
          );
        }

        return <Navigate to="/dashboard/residents" replace />;
      // THE LEGACY ADDRESSES. Bookmarks, links in sent invitations and browser
      // history all still point here.
      case '/dashboard/tenants':
      case '/dashboard/tenants/':
        return <Navigate to="/dashboard/residents" replace />;
      case '/dashboard/tenants/add-new-tenant':
      case '/dashboard/tenants/add-new-tenant/':
        return <Navigate to="/dashboard/residents/add-new-resident" replace />;
      case '/dashboard/portfolios':
      case '/dashboard/portfolios/':
        if (canReadPortfolio) {
          return <DashboardPortfolios />;
        }

        return (
          <RedirectWithReason
            to="/dashboard"
            reason="Portfolios are not available for your role. Here is your dashboard."
          />
        );
      case '/dashboard/units':
      case '/dashboard/units/':
        if (canReadUnit) {
          return <DashboardUnits />;
        }

        return (
          <RedirectWithReason
            to="/dashboard"
            reason="Units are not available for your role. Here is your dashboard."
          />
        );
      // The front-desk roster the Units screen links out to. Occupancy-gated
      // rather than unit-gated: it is the resident list, not the door list.
      case '/dashboard/roster':
      case '/dashboard/roster/':
        if (canReadOccupancy) {
          return <DashboardBuildingRoster />;
        }

        return (
          <RedirectWithReason
            to="/dashboard"
            reason="The roster is not available for your role. Here is your dashboard."
          />
        );
      case '/dashboard/shifts':
      case '/dashboard/shifts/':
        if (canReadShifts) {
          return <DashboardShifts />;
        }

        return (
          <RedirectWithReason
            to="/dashboard"
            reason="Shifts are not available for your role. Here is your dashboard."
          />
        );
      case '/dashboard/staff-access':
      case '/dashboard/staff-access/':
        if (canReadScope) {
          return <DashboardScopes />;
        }

        return (
          <RedirectWithReason
            to="/dashboard"
            reason="Staff access is not available for your role. Here is your dashboard."
          />
        );
      case '/dashboard/visitor-passes':
      case '/dashboard/visitor-passes/':
        if (canReadPasses) {
          return <DashboardPasses />;
        }

        return (
          <RedirectWithReason
            to="/dashboard"
            reason="Visitor passes are not available for your role. Here is your dashboard."
          />
        );
      case '/dashboard/stays':
      case '/dashboard/stays/':
        if (canReadStays) {
          return <DashboardStays />;
        }

        return (
          <RedirectWithReason
            to="/dashboard"
            reason="Stays is not available for your role. Here is your dashboard."
          />
        );
      // Plan S5: calendar changes waiting for the host (?building= / ?listing= filters).
      case '/dashboard/stays/sync-review':
      case '/dashboard/stays/sync-review/':
        if (canReadStays) {
          return <SyncReviewPage />;
        }

        return (
          <RedirectWithReason
            to="/dashboard"
            reason="Stays is not available for your role. Here is your dashboard."
          />
        );
      // Plan S8: the organisation's message templates (stays:manage).
      case '/dashboard/stays/messages':
      case '/dashboard/stays/messages/':
        if (canManageStays) {
          return <MessageLibraryPage />;
        }

        return (
          <RedirectWithReason
            to="/dashboard/stays"
            reason="Message templates are for owners and property managers. Here are your listings."
          />
        );
      // Independent Stays IS2: a Stays-only host's properties (no Desk only —
      // with Desk the same rows are Buildings and Units).
      case '/dashboard/stays/properties':
      case '/dashboard/stays/properties/':
        if (canReadStays && canPromoteStays && staysOnlyAccount(user)) {
          return <PropertiesPage />;
        }

        return (
          <RedirectWithReason
            to={canReadStays ? '/dashboard/stays' : '/dashboard'}
            reason="Properties are where a Lobbero Stays account keeps its places. Your account uses Buildings and Units."
          />
        );
      // Independent Stays IS4: Stays billing (billing:read, an account with Stays).
      case '/dashboard/stays/billing':
      case '/dashboard/stays/billing/':
        if (canReadStays && canReadBilling) {
          return <StaysBillingPage />;
        }

        return (
          <RedirectWithReason
            to={canReadStays ? '/dashboard/stays' : '/dashboard'}
            reason="Stays billing is for the account owner. Here are your listings."
          />
        );
      // Independent Stays IS8: Your data (download everything, close) — the owner of
      // a Stays-only account. The server refuses anyone else as well.
      case '/dashboard/stays/your-data':
      case '/dashboard/stays/your-data/':
        if (canReadStays && canManageBilling && !hasDesk) {
          return <YourDataPage />;
        }

        return (
          <RedirectWithReason
            to={canReadStays ? '/dashboard/stays' : '/dashboard'}
            reason="Your data is for the owner of a Lobbero Stays account. Here are your listings."
          />
        );
      // Independent Stays IS5: the team (stays:read + user:read, as GET /stays/team).
      case '/dashboard/stays/team':
      case '/dashboard/stays/team/':
        if (canReadStays && canReadUsers) {
          return <TeamPage />;
        }

        return (
          <RedirectWithReason
            to={canReadStays ? '/dashboard/stays' : '/dashboard'}
            reason="The team is for the account owner and property managers. Here are your listings."
          />
        );
      // Independent Stays IS5: a co-host's guest cards across their listings.
      case '/dashboard/stays/guest-cards':
      case '/dashboard/stays/guest-cards/':
        if (canReadStays && canReadGuestCards) {
          return <GuestCardsPage />;
        }

        return (
          <RedirectWithReason
            to={canReadStays ? '/dashboard/stays' : '/dashboard'}
            reason="Guest cards are not available for your role. Here are your listings."
          />
        );
      case '/dashboard/billing':
      case '/dashboard/billing/':
        /* HIDING THE LINK IS NOT A GUARD. The menu entry is gated now, but a
           bookmark and a typed address both reach this switch, and the roles
           probe confirmed the screen rendered for a property manager who holds
           no billing permission at all. Same bounce every other gated section
           here uses. */
        if (canReadBilling) {
          return <DashboardBilling />;
        }

        return (
          <RedirectWithReason
            to="/dashboard"
            reason="Billing is not available for your role. Here is your dashboard."
          />
        );
      /* NO SEGMENT IS THE DASHBOARD. /dashboard is where the sidebar's own logo
         links, and it was Home.

         AN UNKNOWN SEGMENT IS A 404, and telling the two apart is the point
         (WP-31). This `default` used to answer both, so every mistyped or
         retired address under /dashboard/* rendered the Dashboard home — the
         reader pressed something, arrived somewhere they did not choose, and was
         told nothing. The real 404 route exists (app-router.tsx:339) and can
         never be reached from here: react-router v6 ranks by specificity, so
         '/dashboard/*' outranks '*' and this switch is the last thing that sees
         the path.

         The discriminator is exact-match on the two spellings of "no segment",
         which is what the switch above could not express. */
      default:
        if (currentUrl === '/dashboard' || currentUrl === '/dashboard/') {
          return <DashboardScreen />;
        }

        return <NotFound />;
    }
  };

  const contentRef = useRef<HTMLDivElement>(null);
  /* The list screens keep a wide table at exactly 768, so this column scrolls
     sideways and used to say nothing. Same hook the tab strip and the KPI
     carousel use — see DashboardContentContainer for the measurements. */
  const contentEdges = useScrollEdges(contentRef);
  const dashboardTitle = dashboardTitleForPath(location.pathname);
  /* WHAT COUNTS AS "A DIFFERENT SCREEN" for the content transition: the nav
     SECTION, not the path. The add-user and add-resident routes render their
     list AND a modal from an unkeyed fragment (below), and keying on the path
     would make the list exit while the modal opens over it. Keyed on the
     section, /dashboard/users -> /dashboard/users/add-new-user is the same
     layer and only the modal animates. Anything the resolver does not know
     (the 404) falls back to the path so it still gets its own layer. */
  const sectionKey =
    dashboardSectionForPath(location.pathname) || location.pathname;

  // Rendered once, up here, so the heading can be suppressed for the 404 (WP-31).
  // dashboardTitleForPath falls back to "Dashboard" for an address that matches
  // nothing, and printing that above "That page is not here" would be the same
  // misdirection the 404 was added to stop. Read off the returned element rather
  // than re-deriving "is this path known?", which would be the switch above
  // written a second time and free to drift from it.
  const content = renderDashboardContentAccordingUrl(location.pathname);
  const isNotFound = React.isValidElement(content) && content.type === NotFound;

  // Reset the persistent content scroller before the next route paints. The
  // old keyed wrapper remounted solely to achieve this, but also replayed a
  // whole-pane slide on top of each screen's own entrance animation.
  useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [location.pathname]);

  return (
    <LayoutContainer dashboard id="dashboard-layout">
      <ContentWrapper dashboard={true}>
        <NavbarAndContentContainer id="dashboard-sidebar">
          <DashboardMainMenu />
          <ContentAndButtonsContainer dashboard={true}>
            {!isNotFound && <DashboardHeading title={dashboardTitle} />}
            <DashboardContentContainer
              ref={contentRef}
              $fadeEnd={contentEdges.canScroll && !contentEdges.atEnd}
            >
              {/* The rail and heading stay put; only this layer crossfades
                  when the section changes (direction 'none': the dashboard's
                  sections are siblings, not a journey). */}
              <ContentTransition
                contentKey={sectionKey}
                direction="none"
                style={CONTENT_LAYER_STYLE}
              >
                {content}
              </ContentTransition>
            </DashboardContentContainer>
          </ContentAndButtonsContainer>
        </NavbarAndContentContainer>
      </ContentWrapper>
    </LayoutContainer>
  );
};
